import { CLASSIC_SYMBOLS } from '../shared/classic-symbols';
import { DEFAULT_CARD_BACK, type CardCollection } from '../shared/collections';
import { CLASSIC_GAME_ID, LEGACY_CLASSIC_GAME_ID, createClassicCollection } from './asset-catalog';
import { collectionStore, type CollectionsState } from './collections-store';
import { getLocale, type Locale } from './i18n';

type RefreshState = Pick<CollectionsState, 'collections' | 'selectedId' | 'upsertCollection' | 'deleteCollection' | 'selectCollection'>;
interface RefreshOptions {
  store?: { getState(): RefreshState };
  locale?: Locale;
  load?: (locale: Locale) => Promise<CardCollection>;
}

/** Personal edits and imported copies stay independent. Only the untouched
 * installed starter edition can be replaced automatically with its new art. */
export function isUnmodifiedLegacyClassic(game: CardCollection): boolean {
  return game.id === LEGACY_CLASSIC_GAME_ID && game.order === 7 &&
    ['Classique', 'Clássico'].includes(game.name) &&
    game.back.kind === 'logo' && game.back.color === DEFAULT_CARD_BACK.color &&
    game.symbols.length === CLASSIC_SYMBOLS.length && game.symbols.every((symbol, id) => {
      const builtin = CLASSIC_SYMBOLS[id];
      return symbol.id === id && symbol.assetKey === `classic:${builtin.slug}` &&
        (symbol.name === builtin.name || symbol.name === builtin.pt) &&
        (!symbol.crop || (symbol.crop.zoom === 1 && symbol.crop.x === 0 && symbol.crop.y === 0));
    });
}

export async function refreshInstalledClassic({ store = collectionStore, locale = getLocale(), load = createClassicCollection }: RefreshOptions = {}): Promise<boolean> {
  const original = store.getState().collections.find(game => game.id === LEGACY_CLASSIC_GAME_ID);
  if (!original || !isUnmodifiedLegacyClassic(original)) return false;
  let latest = store.getState().collections.find(game => game.id === CLASSIC_GAME_ID);
  if (!latest) latest = await load(locale);
  // A name/crop/back edit or deletion while the images load wins over migration.
  if (store.getState().collections.find(game => game.id === original.id) !== original) return false;
  if (!store.getState().collections.some(game => game.id === CLASSIC_GAME_ID)) store.getState().upsertCollection(latest);
  if (store.getState().selectedId === original.id) store.getState().selectCollection(CLASSIC_GAME_ID);
  store.getState().deleteCollection(original.id);
  return true;
}
